import React, { useState } from "react";
import { motion } from "framer-motion";
import { User, KeyRound } from "lucide-react";
import { LangToggle } from "../components/Shared";
import { translations } from "@/i18n";

export default function Welcome({ onStart, lang, setLang }) {
  const T = translations[lang];
  const [title, setTitle] = useState("Mr");
  const [name, setName] = useState("");
  const [room, setRoom] = useState("");

  const valid = name.trim().length >= 2 && room.length >= 1;

  const handleRoom = (v) => setRoom(v.replace(/\D/g, "").slice(0, 4));

  const submit = () => {
    if (!valid) return;
    onStart(title, name.trim(), room);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen flex flex-col px-7 py-8 bg-gradient-to-b from-white to-[#F0F7FF]"
      data-testid="welcome-screen"
    >
      <div className="flex justify-end">
        <LangToggle lang={lang} setLang={setLang} variant="dark" />
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-[400px] w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="flex flex-col items-center text-center"
        >
          <img src="/logo-tulip.png" alt="Golden Tulip" className="w-20 h-20 object-contain mb-3" />
          <h1 className="font-playfair font-bold text-[30px] leading-none text-[#C9A84C] tracking-wide">
            GOLDEN TULIP
          </h1>
          <p className="font-inter text-[13px] tracking-[2px] text-[#4AADDF] mt-2 font-medium">
            HOLLAND RESORT BATU
          </p>
          <div className="w-[60px] h-[1px] bg-[#C9A84C] my-5" />
          <p className="font-inter italic text-[15px] text-[#6B7B8D] leading-relaxed">{T.tagline}</p>
        </motion.div>

        <div className="h-7" />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="space-y-4"
        >
          {/* Title Mr/Mrs */}
          <div className="flex gap-2">
            {["Mr", "Mrs"].map((t) => (
              <button
                key={t}
                data-testid={`title-${t.toLowerCase()}`}
                onClick={() => setTitle(t)}
                className={`flex-1 h-[48px] rounded-[12px] font-medium text-[15px] transition-all duration-200 border ${
                  title === t
                    ? "bg-[#4AADDF] text-white border-[#4AADDF] shadow-[0_4px_12px_rgba(74,173,223,0.3)]"
                    : "bg-white text-[#6B7B8D] border-[#E0E8F0]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="relative">
            <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7B8D]" />
            <input
              data-testid="input-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={T.namePlaceholder}
              className="w-full bg-white border border-[#E0E8F0] rounded-[12px] h-[52px] pl-12 pr-4 text-[16px] text-[#1A1A2E] placeholder-[#6B7B8D] transition-all duration-200 focus:outline-none focus:border-[#4AADDF] focus:ring-[3px] focus:ring-[#4AADDF]/20"
            />
          </div>
          <div className="relative">
            <KeyRound size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7B8D]" />
            <input
              data-testid="input-room"
              value={room}
              onChange={(e) => handleRoom(e.target.value)}
              inputMode="numeric"
              placeholder={T.roomPlaceholder}
              className="w-full bg-white border border-[#E0E8F0] rounded-[12px] h-[52px] pl-12 pr-4 text-[16px] text-[#1A1A2E] placeholder-[#6B7B8D] transition-all duration-200 focus:outline-none focus:border-[#4AADDF] focus:ring-[3px] focus:ring-[#4AADDF]/20"
            />
          </div>

          <button
            data-testid="start-button"
            onClick={submit}
            disabled={!valid}
            style={{ opacity: valid ? 1 : 0.5 }}
            className="w-full h-[52px] rounded-[12px] bg-gradient-to-r from-[#4AADDF] to-[#367ABD] text-white font-bold text-[16px] shadow-[0_6px_18px_rgba(74,173,223,0.3)] transition-transform duration-200 active:scale-[0.98] disabled:cursor-not-allowed"
          >
            {T.start}
          </button>
        </motion.div>
      </div>

      <p className="text-center text-[12px] text-[#A9B8C6] mt-6">{T.footer}</p>
    </motion.div>
  );
}
